import React, { useRef, useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { UploadMedia } from "@/components/media/UploadMedia";
import { CapturePhoto } from "@/components/media/CapturePhoto";
import { CaptureVideo } from "@/components/media/CaptureVideo";
import { CaptureAudio } from "@/components/media/CaptureAudio";
import {
  type ICapturedGeoLocation,
  generateCaptureFileName,
} from "@/services/media-capture";
import { useThemeColor } from "@/hooks/useThemeColor";
import { CustomAlert } from "@/components/ThemedAlert";
import * as ImagePicker from "expo-image-picker";

/**
 * Properties for the shared CapturePanel component.
 */
export interface ICapturePanelProps {
  /** Callback triggered when media files are selected, dragged & dropped, or captured */
  onUploadFiles: (files: (File | any)[], geoLocation?: ICapturedGeoLocation | null) => Promise<void> | void;
  /** Whether an upload is currently processing */
  uploading?: boolean;
  /** Text description displayed during upload (e.g. "Uploading 1 of 4...") */
  uploadStatusText?: string;
  /** Comma-separated MIME types for the web file picker */
  acceptedTypes?: string;
  /** Label displayed in the drop area during idle resting state */
  dropLabel?: string;
  /** Label displayed in the drop area during active drag-over state */
  activeDropLabel?: string;
  /** Whether interactions on the panel are disabled */
  disabled?: boolean;
  /** Whether to render the Photo, Video, and Audio capture buttons */
  showCaptureButtons?: boolean;
  /** Compact mode (e.g. inside popup selectors or narrow containers) */
  compact?: boolean;
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
}

/**
 * Standardized Archival Heirloom Media Capture & Drop Panel component.
 * Provides a unified drop area with interactive hover feedback, drag-and-drop file ingestion,
 * and high-resolution Photo, Video, and Audio capture triggers adhering to Memories UX Standards.
 *
 * @param {ICapturePanelProps} props - Capture panel properties.
 * @returns {React.JSX.Element} The rendered CapturePanel component.
 */
export function CapturePanel({
  onUploadFiles,
  uploading = false,
  uploadStatusText = "Uploading...",
  acceptedTypes = "image/*,video/*,audio/*,application/pdf,.pdf,.doc,.docx,.txt,.rtf,.odt,.csv,.xlsx",
  dropLabel,
  activeDropLabel = "Release to Upload",
  disabled = false,
  showCaptureButtons = true,
  compact = false,
  style,
}: ICapturePanelProps): React.JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resolvedDropLabel =
    dropLabel ??
    (compact
      ? "Drop files or Browse"
      : Platform.OS === "web"
      ? "Drop Media, Documents, or PDFs to Upload"
      : "Tap to Select Photos, Videos & Documents");

  const cardBg = useThemeColor({}, "card");

  // Web window drag-and-drop event listeners
  useEffect(() => {
    if (Platform.OS !== "web" || disabled) return;

    const handleDragOver = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = async (e: DragEvent): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        onUploadFiles(Array.from(files));
      }
    };

    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [disabled, onUploadFiles]);

  /**
   * Opens file selection (web file dialog or native image/video picker).
   */
  const handleBrowseFiles = async (): Promise<void> => {
    if (disabled || uploading) return;
    if (Platform.OS === "web") {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== ImagePicker.PermissionStatus.GRANTED && (status as any) !== "granted") {
        CustomAlert.alert("Permission Required", "Please grant photo and video library access to select media.");
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images", "videos"],
        allowsMultipleSelection: true,
        quality: 1.0,
        exif: true,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      await handleMediaUploadSelected(result.assets);
    } catch (err: any) {
      console.error("[CapturePanel] Native library picker error:", err);
      CustomAlert.alert("Picker Error", err.message || "Failed to select media files.");
    }
  };

  /**
   * Processes media assets selected via the UploadMedia component from media collections.
   */
  const handleMediaUploadSelected = async (assets: any[]): Promise<void> => {
    if (!assets || assets.length === 0) return;

    const fileItems = assets.map((asset) => {
      const isVideo = asset.type === "video" || (asset.mimeType && asset.mimeType.startsWith("video/"));
      const defaultMime = isVideo ? "video/mp4" : "image/jpeg";
      const mime = asset.mimeType || defaultMime;
      const defaultName = isVideo ? generateCaptureFileName("video", mime) : generateCaptureFileName("photo", mime);
      return {
        name: asset.fileName || defaultName,
        type: mime,
        size: asset.fileSize || 0,
        uri: asset.uri,
        exif: asset.exif,
        lastModified: asset.modificationTime ? (asset.modificationTime < 1e11 ? asset.modificationTime * 1000 : asset.modificationTime) : undefined,
        creationTime: asset.creationTime ? (asset.creationTime < 1e11 ? asset.creationTime * 1000 : asset.creationTime) : undefined,
        width: asset.width,
        height: asset.height,
        duration: asset.duration,
      };
    });

    await onUploadFiles(fileItems as any);
  };

  const handleMouseEnter = (): void => {
    if (!disabled && !uploading) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = (): void => {
    setIsHovered(false);
  };

  const isAndroid =
    Platform.OS === "android" ||
    (Platform.OS === "web" && typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || ""));

  const showDropZone = !isAndroid;

  return (
    <View style={[styles.captureHeaderBar, { backgroundColor: cardBg }, style]}>
      {/* Left: Interactive Drop Media Area (Hidden on Android where native library pickers and camera controls are used) */}
      {showDropZone && (
        <TouchableOpacity
          style={[
            styles.dropZone,
            isHovered && !uploading && styles.dropZoneHovered,
            isDragging && styles.dropZoneActive,
            uploading && styles.dropZoneUploading,
            disabled && styles.dropZoneDisabled,
            Platform.OS === "web" &&
              ({
                cursor: disabled || uploading ? "default" : "pointer",
                transition: "border-color 0.15s ease, background-color 0.15s ease",
              } as any),
          ]}
          onPress={handleBrowseFiles}
          onHoverIn={handleMouseEnter}
          onHoverOut={handleMouseLeave}
          {...Platform.select({
            web: {
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
              onPointerEnter: handleMouseEnter,
              onPointerLeave: handleMouseLeave,
            } as any,
            default: {},
          })}
          activeOpacity={0.8}
          accessibilityLabel="Drop media here or click to browse files"
          accessibilityRole="button"
          disabled={disabled || uploading}
        >
          {uploading ? (
            <View style={styles.uploadingContainer}>
              <View style={{ width: 140, marginBottom: 4 }}>
                <ThemedProgressBar loading={uploading} />
              </View>
              <ThemedText style={styles.dropZoneText} numberOfLines={1} ellipsizeMode="tail">
                {uploadStatusText}
              </ThemedText>
            </View>
          ) : (
            <View style={styles.dropZoneInner}>
              <Ionicons name="cloud-upload-outline" size={18} color="#4A90E2" />
              <ThemedText style={styles.dropZoneText} numberOfLines={1} ellipsizeMode="tail">
                {isDragging ? activeDropLabel : resolvedDropLabel}
              </ThemedText>
            </View>
          )}
        </TouchableOpacity>
      )}

      {/* Uploading progress indicator for platforms where dropZone is hidden (e.g. Android) */}
      {!showDropZone && uploading && (
        <View style={[styles.uploadingContainer, { flex: 1, paddingHorizontal: 8 }]}>
          <View style={{ width: 140, marginBottom: 4 }}>
            <ThemedProgressBar loading={uploading} />
          </View>
          <ThemedText style={styles.dropZoneText} numberOfLines={1} ellipsizeMode="tail">
            {uploadStatusText}
          </ThemedText>
        </View>
      )}

      {/* Right: Capture & Upload Media Controls */}
      {showCaptureButtons && (
        <View style={[styles.captureOptionsContainer, isAndroid && { flex: 1 }]}>
          <UploadMedia
            onMediaSelected={handleMediaUploadSelected}
            mediaTypes={["images", "videos"]}
            allowsMultipleSelection={true}
            disabled={disabled}
            uploading={uploading}
            title="Upload"
            style={[styles.captureOptionBtn, isAndroid && { flex: 1 }]}
            textStyle={styles.captureOptionText}
          />

          {!compact && (
            <>
              <CapturePhoto
                onCapture={(file, loc) => onUploadFiles([file], loc)}
                disabled={disabled}
                uploading={uploading}
                style={[styles.captureOptionBtn, isAndroid && { flex: 1 }]}
                textStyle={styles.captureOptionText}
              />

              <CaptureVideo
                onCapture={(file, loc) => onUploadFiles([file], loc)}
                disabled={disabled}
                uploading={uploading}
                style={[styles.captureOptionBtn, isAndroid && { flex: 1 }]}
                textStyle={styles.captureOptionText}
              />

              <CaptureAudio
                onCapture={(file, loc) => onUploadFiles([file], loc)}
                disabled={disabled}
                uploading={uploading}
                style={[styles.captureOptionBtn, isAndroid && { flex: 1 }]}
                textStyle={styles.captureOptionText}
              />
            </>
          )}
        </View>
      )}

      {/* Hidden file input for web click-to-browse */}
      {Platform.OS === "web" &&
        React.createElement("input", {
          ref: fileInputRef,
          type: "file",
          multiple: true,
          accept: acceptedTypes,
          style: { display: "none" },
          onChange: (e: any) => {
            if (e.target.files && e.target.files.length > 0) {
              onUploadFiles(Array.from(e.target.files));
              e.target.value = "";
            }
          },
        })}
    </View>
  );
}

export { UploadMedia } from "@/components/media/UploadMedia";
export { CapturePhoto } from "@/components/media/CapturePhoto";
export { CaptureVideo } from "@/components/media/CaptureVideo";
export { CaptureAudio } from "@/components/media/CaptureAudio";
export default CapturePanel;

const styles = StyleSheet.create({
  captureHeaderBar: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 8,
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 6,
    width: "100%",
    boxSizing: "border-box" as any,
  },
  dropZone: {
    flex: 1,
    minWidth: 0,
    height: 48,
    borderWidth: 1.5,
    borderRadius: 4,
    borderStyle: "dashed",
    borderColor: "#4A90E2",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(74, 144, 226, 0.05)",
    boxSizing: "border-box" as any,
    paddingHorizontal: 8,
    overflow: "hidden",
  },
  dropZoneHovered: {
    borderColor: "#2173DF",
    backgroundColor: "rgba(74, 144, 226, 0.12)",
    borderStyle: "solid",
  },
  dropZoneActive: {
    backgroundColor: "rgba(74, 144, 226, 0.18)",
    borderColor: "#2173DF",
    borderStyle: "solid",
  },
  dropZoneUploading: {
    opacity: 0.6,
  },
  dropZoneDisabled: {
    opacity: 0.4,
  },
  dropZoneInner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: "100%",
  },
  dropZoneText: {
    fontSize: 12,
    color: "#4A90E2",
    fontWeight: "600",
    flexShrink: 1,
  },
  captureOptionsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: Platform.OS === "android" ? 1 : undefined,
  },
  captureOptionBtn: {
    height: 48,
    paddingHorizontal: 12,
    borderRadius: 0,
    borderWidth: 1,
    marginVertical: 0,
    flex: Platform.OS === "android" ? 1 : undefined,
  },
  captureOptionText: {
    fontSize: 11,
    fontWeight: "600",
  },
  uploadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
});
